import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 12;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          confetti({ particleCount: 140, spread: 90, origin: { y: 0.5 }, colors: ["#E9E604", "#C9C600", "#F59E0B"] });
          setState("paid");
          setTimeout(() => navigate("/", { replace: true }), 2500);
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") { setState("error"); return; }
      } catch (e) { /* keep trying */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, navigate, refreshUser]);

  return (
    <div className="min-h-screen grain flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full text-center p-8 rounded-3xl border border-[#242417] bg-[#12141C] shadow-2xl" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-emerald-400 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-2xl font-black mb-2">Confirming your payment…</h1>
            <p className="text-white">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center mx-auto mb-5 glow-emerald">
              <CheckCircle2 className="w-9 h-9 text-black" />
            </div>
            <h1 className="font-display text-3xl font-black mb-2">Welcome to Pro! 🎉</h1>
            <p className="text-white">You're all set. Taking you to your dashboard…</p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 text-amber-400 mx-auto mb-5" />
            <h1 className="font-display text-2xl font-black mb-2">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-white mb-6">
              {state === "timeout"
                ? "Your payment is taking longer than usual. If you were charged, your access will unlock shortly."
                : "We couldn't confirm this payment."}
            </p>
            <Link to="/upgrade" data-testid="back-to-upgrade" className="inline-block h-11 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold leading-[2.75rem]">
              Back to plans
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen grain flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center p-8 rounded-3xl border border-[#242417] bg-[#12141C] shadow-2xl" data-testid="payment-cancel-page">
        <XCircle className="w-14 h-14 text-white mx-auto mb-5" />
        <h1 className="font-display text-2xl font-black mb-2">Checkout cancelled</h1>
        <p className="text-white mb-6">No charge was made. You can subscribe whenever you're ready.</p>
        <Link to="/upgrade" data-testid="cancel-back-to-upgrade" className="inline-block h-11 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold leading-[2.75rem]">
          Back to plans
        </Link>
      </div>
    </div>
  );
}
